import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { toggleVariants } from '@/components/ui/toggle';
import { cn } from '@/lib/cn';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import type { Level } from '@tiptap/extension-heading';
import type { Editor } from '@tiptap/react';
import type { VariantProps } from 'class-variance-authority';
import { CaseSensitive } from 'lucide-react';
import * as React from 'react';
import type { FormatAction } from '../../types';
import { ShortcutKey } from '../shortcut-key';
import { ToolbarButton } from '../toolbar-button';

import type { JSX } from "react";

interface TextStyle
  extends Omit<
    FormatAction,
    'value' | 'icon' | 'action' | 'isActive' | 'canExecute'
  > {
  element: keyof JSX.IntrinsicElements;
  level?: Level;
  className: string;
}

const formatActions: TextStyle[] = [
  {
    label: 'Vanlig text',
    element: 'span',
    className: 'grow',
    shortcuts: ['mod', 'alt', '0'],
  },
  {
    label: 'Rubrik 1',
    element: 'h1',
    level: 1,
    className: 'm-0 grow text-[28px] font-extrabold',
    shortcuts: ['mod', 'alt', '1'],
  },
  {
    label: 'Rubrik 2',
    element: 'h2',
    level: 2,
    className: 'm-0 grow text-[24.3754px] font-bold',
    shortcuts: ['mod', 'alt', '2'],
  },
  {
    label: 'Rubrik 3',
    element: 'h3',
    level: 3,
    className: 'm-0 grow text-[21.22px] font-semibold',
    shortcuts: ['mod', 'alt', '3'],
  },
  {
    label: 'Rubrik 4',
    element: 'h4',
    level: 4,
    className: 'm-0 grow text-[18.4731px] font-semibold',
    shortcuts: ['mod', 'alt', '4'],
  },
  {
    label: 'Rubrik 5',
    element: 'h5',
    level: 5,
    className: 'm-0 grow text-[16.0818px] font-normal',
    shortcuts: ['mod', 'alt', '5'],
  },
  {
    label: 'Rubrik 6',
    element: 'h6',
    level: 6,
    className: 'm-0 grow text-[14px] font-normal',
    shortcuts: ['mod', 'alt', '6'],
  },
];

interface FormatSectionProps extends VariantProps<typeof toggleVariants> {
  editor: Editor;
  activeLevels?: Level[];
}

export const FormatSection: React.FC<FormatSectionProps> = React.memo(
  ({ editor, activeLevels = [1, 2, 3, 4, 5, 6], size, variant }) => {
    const filteredActions = React.useMemo(
      () =>
        formatActions.filter(
          (action) => !action.level || activeLevels.includes(action.level)
        ),
      [activeLevels]
    );

    const handleStyleChange = React.useCallback(
      (level?: Level) => {
        if (level) {
          editor.chain().focus().toggleHeading({ level }).run();
        } else {
          editor.chain().focus().setParagraph().run();
        }
      },
      [editor]
    );

    const renderMenuItem = React.useCallback(
      ({ label, element: Element, level, className, shortcuts }: TextStyle) => (
        <DropdownMenuItem
          key={label}
          onClick={() => handleStyleChange(level)}
          className={cn(
            'my-1 flex flex-row items-center justify-between gap-4',
            {
              'bg-accent': level
                ? editor.isActive('heading', { level })
                : editor.isActive('paragraph'),
            }
          )}
          aria-label={label}
        >
          <Element className={className}>{label}</Element>
          {shortcuts?.length && <ShortcutKey keys={shortcuts} />}
        </DropdownMenuItem>
      ),
      [editor, handleStyleChange]
    );

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <ToolbarButton
            isActive={editor.isActive('heading')}
            tooltip='Format'
            aria-label='Format'
            pressed={editor.isActive('heading')}
            className='w-12'
            disabled={editor.isActive('codeBlock')}
            size={size}
            variant={variant}
          >
            <CaseSensitive className='size-5' />
            <ChevronDownIcon className='size-5' />
          </ToolbarButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='start'
          className='w-full'
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          {filteredActions.map(renderMenuItem)}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);

FormatSection.displayName = 'FormatSection';
